<template>
    <div>
        <hox-alert type="info" class="importExportModal__howto" size="small">
            <template #title>To upload content</template>
            <template #content>
                <div>
                    <p
                        class="link-like"
                        data-testid="import-export-modal__download-template-link-selection-tab"
                        @click="downloadTemplate"
                    >
                        1. Download template
                    </p>
                    <p>2. Fill in the content in the file</p>
                    <p>3. Upload the content XLSX file</p>
                </div>
            </template>
        </hox-alert>

        <div class="importExportModal__upload-controls">
            <XlsUploadButton
                data-testid="import-export-modal__upload-template-selection-tab"
                @click.stop="resetUpload"
                @load="onXlsLoad"
                @error="onImportError"
            />
        </div>

        <div class="importExportModal__upload-results">
            <div
                v-if="fileName"
                class="importExportModal__upload-result__file"
                data-testid="import-export-modal__uploaded-file-name"
            >
                {{ fileName }} ({{ validRows.length }} rows)
            </div>

            <hox-alert
                v-if="hasError"
                type="danger"
                class="importExportModal__upload-result"
                data-testid="import-export-modal__upload-error"
            >
                <template #title>There were problems reading this sheet</template>

                <template #content>
                    <ul v-if="errorDetails.length" class="importExportModal__upload-results-error-list">
                        <li v-for="detail in errorDetails" :key="detail">{{ detail }}</li>
                    </ul>
                </template>
            </hox-alert>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";

import XlsUploadButton from "@/components/Campaign/XlsUploadButton.vue";
import { useGA4Tracking } from "@/composables/useGA4Tracking";
import type { DeliverableIdentifier } from "@/enums/library";
import GA4 from "@/enums/ga4Events";
import { getImportFileTypeError, ImportFileType } from "@/utils/importFileType";

import type {
    ImportedSheet,
    ImportedXlsData,
    ImportRow,
    SelectionTabState,
    XlsUploadError
} from "../../../types/importExportTypes";
import { useDeliverableImport } from "./useDeliverableImport";

interface SelectionTabData {
    fileName: string;
    hasError: boolean;
    errorDetails: string[];
    validRows: DeliverableIdentifier[];
    reportingLabels: string[];
    uploadCompleted: boolean;
}

export default defineComponent({
    name: "ImportExportLibraryModalSelectionTab",

    components: {
        XlsUploadButton
    },

    emits: ["exportTemplate", "change"],

    setup() {
        const ga4 = useGA4Tracking();
        const { getValidationError, processRow } = useDeliverableImport();

        return { ga4, getValidationError, processRow };
    },

    data(): SelectionTabData {
        return {
            fileName: "",
            hasError: false,
            errorDetails: [],
            validRows: [],
            reportingLabels: [],
            uploadCompleted: false
        };
    },

    methods: {
        emitState() {
            const state: SelectionTabState = {
                isReady: this.uploadCompleted && !this.hasError,
                validRows: this.validRows,
                reportingLabels: this.reportingLabels
            };

            this.$emit("change", state);
        },

        resetUpload() {
            this.uploadCompleted = false;
            this.emitState();
        },

        downloadTemplate() {
            this.ga4.trackClickEvent({
                interactionObject: GA4.CLICK_INTERACTION_OBJECTS.LINK,
                interactionText: GA4.CLICK_INTERACTION_TEXTS.DOWNLOAD_TEMPLATE
            });
            this.$emit("exportTemplate");
        },

        onImportError(error: XlsUploadError) {
            this.hasError = true;
            this.errorDetails = [error.message];
            this.emitState();
        },

        async onXlsLoad(importedData: ImportedXlsData) {
            this.uploadCompleted = false;
            this.hasError = false;
            this.errorDetails = [];

            if (!importedData.sheets.length) {
                this.errorDetails = [
                    "The file is either empty or is incorrectly formatted. Please check your data and try again."
                ];
                this.hasError = true;
                this.emitState();
                return;
            }

            const fileTypeError = getImportFileTypeError(importedData, ImportFileType.Selection, { strict: true });
            if (fileTypeError) {
                this.fileName = "";
                this.validRows = [];
                this.errorDetails = [fileTypeError];
                this.hasError = true;
                this.emitState();
                return;
            }

            await this.$nextTick();

            const inputSheet = importedData.Input as ImportedSheet | undefined;
            this.processInputSelect(inputSheet?.sheetData ?? []);

            this.ga4.trackFeatureEvent({
                featureEvent: GA4.EVENT_TYPES.FEATURE_COMPLETE,
                featureCategory: GA4.FEATURE_CATEGORIES.IMPORT_EXPORT,
                featureContext: GA4.FEATURE_CONTEXTS.IMPORT_EXPORT
            });

            this.uploadCompleted = true;
            this.fileName = importedData.fileName;
            this.emitState();
        },

        processInputSelect(dataArray: ImportRow[]) {
            this.validRows = [];
            this.hasError = false;
            this.errorDetails = [];
            // empty value represents templateName header
            const [, , ...groupValueNames] = (dataArray.shift() ?? []) as string[];
            groupValueNames.pop();

            const seed = {
                validRows: [] as DeliverableIdentifier[],
                sameValueIndexes: {} as Record<string, number[]>,
                reportingLabels: new Set<string>()
            };

            const { validRows, sameValueIndexes, reportingLabels } = dataArray.reduce((acc, row, i) => {
                const rowIndex = i + 3;
                const validationError = this.getValidationError(row, groupValueNames, rowIndex);
                const valueString = JSON.stringify(row);
                if (acc.sameValueIndexes[valueString]) {
                    acc.sameValueIndexes[valueString].push(rowIndex);
                } else {
                    acc.sameValueIndexes[valueString] = [rowIndex];
                }

                const reportingLabelIndex = 2 + groupValueNames.length;
                const reportingLabel = row[reportingLabelIndex];
                if (reportingLabel) {
                    reportingLabel
                        .split(",")
                        .map(label => label.trim())
                        .filter(Boolean)
                        .forEach(label => acc.reportingLabels.add(label));
                }

                if (validationError.length) {
                    this.hasError = true;
                    this.errorDetails.push(...validationError);
                    return acc;
                }

                acc.validRows.push(this.processRow(row, groupValueNames));
                return acc;
            }, seed);

            Object.entries(sameValueIndexes).forEach(([values, indexes]) => {
                if (indexes.length > 1) {
                    this.hasError = true;
                    this.errorDetails.push(
                        `Rows ${indexes.join(", ")} have duplicated values (${(JSON.parse(values) as unknown[]).join(", ")})`
                    );
                }
            });

            this.validRows = validRows;
            this.reportingLabels = [...reportingLabels.values()];
            this.emitState();
        }
    }
});
</script>
